import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Menu, X, User, MessageCircle, LogOut } from 'lucide-react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import NotificationBell from './NotificationBell';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pendingContacts, setPendingContacts] = useState(0);
  const navigate = useNavigate();

  const getAuthPath = (path) => {
    const currentPath = window.location.pathname + window.location.search;
    const shouldReturn = currentPath.startsWith('/provider/');
    return shouldReturn ? `${path}?returnTo=${encodeURIComponent(currentPath)}` : path;
  };

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await api.get('/auth/me');
      setUser(response.data);

      if (response.data?.role === 'provider') {
        try {
          const crRes = await api.get('/contact-requests/received');
          setPendingContacts((crRes.data || []).filter(r => r.status === 'pending').length);
        } catch {}
      }
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await api.post('/auth/logout');
      localStorage.removeItem('jwt_token');
      setUser(null);
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
      localStorage.removeItem('jwt_token');
      setUser(null);
      navigate('/');
    }
  };

  return (
    <nav className="bg-white shadow-sm sticky top-0 z-50" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-24">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <img src="/logo-petadvisor.svg" alt="PetAdvisor" className="h-14 md:h-16" data-testid="navbar-logo" />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-6">
            {user?.role === 'provider' ? (
              <Link
                to="/provider/dashboard"
                className="relative bg-[#ffff00] hover:bg-[#e6e600] text-[#000000] text-lg font-bold transition-colors px-5 py-3 rounded-full shadow-sm"
              >
                Solicitudes Públicas
              </Link>
            ) : (
              <Link to="/search" className="text-[#000000] hover:text-[#e6e600] text-lg font-semibold transition-colors">
                Buscar Servicios
              </Link>
            )}
            
            {user ? (
              <>
                <Link to="/chat" className="relative text-[#000000] hover:text-[#e6e600] p-2">
                  <MessageCircle className="w-7 h-7" />
                  {pendingContacts > 0 && user?.role === 'provider' && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold min-w-5 h-5 rounded-full flex items-center justify-center px-1">
                      {pendingContacts > 9 ? '9+' : pendingContacts}
                    </span>
                  )}
                </Link>
                <div className="relative group">
                  <button className="flex items-center space-x-2 text-[#000000] hover:text-[#e6e600] py-2">
                    {user.picture ? (
                      <img src={user.picture} alt={user.name} className="w-10 h-10 rounded-full" />
                    ) : (
                      <User className="w-7 h-7" />
                    )}
                    <span className="font-semibold text-lg">{user.name}</span>
                  </button>
                  <div className="absolute right-0 top-full pt-2 w-56 invisible group-hover:visible">
                    <div className="bg-white rounded-lg shadow-lg py-2 border">
                    {user.role === 'provider' ? (
                      <Link to="/provider/account" className="block px-4 py-3 text-[#000000] text-lg hover:bg-gray-50">
                        Mi Cuenta
                      </Link>
                    ) : (
                      <Link to="/account" className="block px-4 py-3 text-[#000000] text-lg hover:bg-gray-50">
                        Mi Cuenta
                      </Link>
                    )}
                    {user.role === 'client' && (
                      <Link to="/dashboard" className="block px-4 py-3 text-[#000000] text-lg hover:bg-gray-50">
                        Panel Cliente
                      </Link>
                    )}
                    {user.role === 'provider' && (
                    <Link to="/historial" className="block px-4 py-3 text-[#000000] text-lg hover:bg-gray-50">
                      Historial y Facturas
                    </Link>
                    )}
                    {user.role === 'provider' && (
                      <Link to="/provider/dashboard" className="block px-4 py-3 text-[#000000] text-lg hover:bg-gray-50">
                        Panel del Negocio
                      </Link>
                    )}
                    {user.role === 'admin' && (
                      <Link to="/admin" className="block px-4 py-3 text-[#000000] text-lg hover:bg-gray-50">
                        Administración
                      </Link>
                    )}
                    <button
                      onClick={handleLogout}
                      className="w-full text-left px-4 py-3 text-[#000000] text-lg hover:bg-gray-50 flex items-center space-x-2"
                    >
                      <LogOut className="w-5 h-5" />
                      <span>Cerrar Sesión</span>
                    </button>
                    </div>
                  </div>
                </div>
                <NotificationBell />
              </>
            ) : (
              !loading && (
                <>
                  <Link to={getAuthPath("/login")}>
                    <Button variant="outline" className="border-2 border-[#000000] text-[#000000] hover:bg-gray-100 text-lg px-6 py-3 h-auto font-semibold">
                      Iniciar Sesión
                    </Button>
                  </Link>
                  <Link to={getAuthPath("/register")}>
                    <Button className="bg-[#ffff00] hover:bg-[#e6e600] text-[#000000] text-lg px-6 py-3 h-auto font-semibold">
                      Registrarse
                    </Button>
                  </Link>
                </>
              )
            )}
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="md:hidden text-[#000000] hover:text-[#e6e600] p-2"
            data-testid="mobile-menu-button"
          >
            {isOpen ? <X className="w-8 h-8" /> : <Menu className="w-8 h-8" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="md:hidden py-4 border-t">
            {user?.role === 'provider' ? (
              <Link to="/provider/dashboard" className="block py-2 text-[#000000] font-bold hover:text-[#ffff00]">
                Solicitudes Públicas
              </Link>
            ) : (
              <Link to="/search" className="block py-2 text-gray-700 hover:text-[#ffff00]">
                Buscar Servicios
              </Link>
            )}
            {user ? (
              <>
                {user.role === 'provider' ? (
                  <Link to="/provider/account" className="block py-2 text-gray-700 hover:text-[#ffff00]">
                    Mi Cuenta
                  </Link>
                ) : (
                  <Link to="/account" className="block py-2 text-gray-700 hover:text-[#ffff00]">
                    Mi Cuenta
                  </Link>
                )}
                {user.role === 'client' && (
                  <Link to="/dashboard" className="block py-2 text-gray-700 hover:text-[#ffff00]">
                    Panel Cliente
                  </Link>
                )}
                {user.role === 'provider' && (
                <Link to="/historial" className="block py-2 text-gray-700 hover:text-[#ffff00]">
                  Historial y Facturas
                </Link>
                )}
                {user.role === 'provider' && (
                  <Link to="/provider/dashboard" className="block py-2 text-gray-700 hover:text-[#ffff00]">
                    Solicitudes Públicas
                  </Link>
                )}
                <Link to="/chat" className="block py-2 text-gray-700 hover:text-[#ffff00]">
                  Chat {pendingContacts > 0 && user?.role === 'provider' && (
                    <span className="ml-2 inline-flex bg-red-500 text-white text-xs font-bold min-w-5 h-5 rounded-full items-center justify-center px-1">
                      {pendingContacts > 9 ? '9+' : pendingContacts}
                    </span>
                  )}
                </Link>
                <button
                  onClick={handleLogout}
                  className="block w-full text-left py-2 text-gray-700 hover:text-[#ffff00]"
                >
                  Cerrar Sesión
                </button>
              </>
            ) : (
              !loading && (
                <>
                  <Link to={getAuthPath("/login")} className="block w-full text-left py-2 text-gray-700 hover:text-[#ffff00]">
                    Iniciar Sesión
                  </Link>
                  <Link to={getAuthPath("/register")} className="block w-full text-left py-2 text-[#ffff00] font-bold">
                    Registrarse
                  </Link>
                </>
              )
            )}
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
